<template>
  <div class="admin-page">
    <div class="admin-toolbar">
      <div class="admin-toolbar__actions">
        <el-input
          v-model="filters.code"
          placeholder="邀请码"
          clearable
          class="filter-input"
          @keyup.enter="fetchList"
        />
        <el-input
          v-model="filters.ownerUuid"
          placeholder="所属用户 UUID"
          clearable
          class="filter-input-wide"
          @keyup.enter="fetchList"
        />
        <el-select v-model="filters.isUsed" placeholder="使用状态" class="filter-select" @change="fetchList">
          <el-option label="全部" :value="-1" />
          <el-option label="未使用" :value="0" />
          <el-option label="已使用" :value="1" />
        </el-select>
        <el-select v-model="filters.issuedByAdmin" placeholder="发放方式" class="filter-select" @change="fetchList">
          <el-option label="全部" :value="-1" />
          <el-option label="用户自生成" :value="0" />
          <el-option label="管理员发放" :value="1" />
        </el-select>
        <el-button type="primary" @click="openBatchDialog">为用户发放</el-button>
        <el-button @click="fetchList">刷新</el-button>
      </div>
    </div>

    <el-table :data="codes" v-loading="loading" style="width: 100%">
      <el-table-column prop="id" label="ID" width="70" />
      <el-table-column prop="code" label="邀请码" width="120" />
      <el-table-column label="所属用户" min-width="200">
        <template #default="scope">
          {{ scope.row.ownerNickname || scope.row.ownerUsername }}
          <div class="muted uuid-line">{{ scope.row.ownerUuid }}</div>
        </template>
      </el-table-column>
      <el-table-column prop="isUsed" label="状态" width="90">
        <template #default="scope">
          <el-tag :type="scope.row.isUsed === 1 ? 'info' : 'success'">
            {{ scope.row.isUsed === 1 ? '已使用' : '未使用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="使用者" min-width="140">
        <template #default="scope">
          <template v-if="scope.row.usedByUuid">
            <div>{{ scope.row.usedByUsername || '—' }}</div>
            <div class="muted uuid-line">{{ scope.row.usedByUuid }}</div>
          </template>
          <span v-else class="muted">—</span>
        </template>
      </el-table-column>
      <el-table-column prop="issuedByAdmin" label="发放" width="100">
        <template #default="scope">
          {{ scope.row.issuedByAdmin === 1 ? '管理员' : '自生成' }}
        </template>
      </el-table-column>
      <el-table-column prop="batchId" label="批次" min-width="120" show-overflow-tooltip />
      <el-table-column label="创建时间" width="170">
        <template #default="scope">{{ formatTime(scope.row.createdAt) }}</template>
      </el-table-column>
    </el-table>

    <AdminPagination
      v-model:current-page="currentPage"
      v-model:page-size="pageSize"
      :page-sizes="[10, 20, 50, 100]"
      :total="total"
      @size-change="fetchList"
      @current-change="fetchList"
    />

    <el-dialog v-model="batchDialogVisible" title="批量发放邀请码" width="520px" destroy-on-close>
      <el-form label-width="110px">
        <el-alert
          type="info"
          :closable="false"
          show-icon
          title="不填写用户 UUID 或用户 ID 时，将为全部未删除用户各发放指定数量的邀请码。"
          style="margin-bottom: 16px"
        />
        <el-form-item label="用户 UUID">
          <el-input v-model="batchForm.userUuid" placeholder="留空则发放给所有人" clearable />
        </el-form-item>
        <el-form-item label="或用户 ID">
          <el-input-number v-model="batchForm.userId" :min="1" :controls="true" style="width: 100%" placeholder="留空则发放给所有人" />
        </el-form-item>
        <el-form-item label="每人数量" required>
          <el-input-number v-model="batchForm.count" :min="1" :max="50" style="width: 100%" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="batchDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="batchSubmitting" @click="submitBatch">生成</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="batchProgressVisible" title="批量发放进度" width="440px" :close-on-click-modal="false">
      <div v-if="batchProgress">
        <p>{{ batchProgress.message }}</p>
        <el-progress
          :percentage="batchProgressPercent"
          :status="batchProgress.status === 'failed' ? 'exception' : batchProgress.status === 'done' ? 'success' : undefined"
        />
        <p class="muted progress-detail">
          用户 {{ batchProgress.usersProcessed || 0 }} / {{ batchProgress.totalUsers || 0 }}，
          已生成 {{ batchProgress.codesCreated || 0 }} 个邀请码
        </p>
      </div>
      <template #footer>
        <el-button v-if="batchProgress?.status === 'processing'" @click="stopBatchPoll(); batchProgressVisible = false">
          后台继续（关闭窗口）
        </el-button>
        <el-button v-else type="primary" @click="closeBatchProgress">完成</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import AdminPagination from '@/components/AdminPagination.vue'
import { authHeaders } from '@/utils/http'

export default {
  name: 'InviteCodeManagement',
  components: { AdminPagination },
  computed: {
    batchProgressPercent() {
      const p = this.batchProgress
      if (!p?.totalUsers) return 0
      return Math.min(100, Math.round((p.usersProcessed / p.totalUsers) * 100))
    }
  },
  data() {
    return {
      loading: false,
      codes: [],
      total: 0,
      currentPage: 1,
      pageSize: 20,
      filters: {
        code: '',
        ownerUuid: '',
        isUsed: -1,
        issuedByAdmin: -1
      },
      batchDialogVisible: false,
      batchSubmitting: false,
      batchForm: { userUuid: '', userId: null, count: 5 },
      batchProgressVisible: false,
      batchProgress: null,
      batchPollTimer: null
    }
  },
  beforeUnmount() {
    this.stopBatchPoll()
  },
  created() {
    this.fetchList()
  },
  methods: {
    formatTime(ts) {
      if (!ts) return '—'
      return new Date(ts * 1000).toLocaleString()
    },
    async fetchList() {
      this.loading = true
      try {
        const payload = {
          page: this.currentPage,
          pageSize: this.pageSize,
          code: this.filters.code || undefined,
          isUsed: this.filters.isUsed,
          issuedByAdmin: this.filters.issuedByAdmin
        }
        if (this.filters.ownerUuid) {
          payload.ownerUuid = this.filters.ownerUuid.trim()
        }
        const res = await axios.post('/api/v1/admin/invite/code/list', payload)
        if (res.data.code !== 200) {
          this.$message.error(res.data.message || '加载失败')
          return
        }
        this.codes = res.data.data?.codes || []
        this.total = res.data.data?.total || 0
      } catch (e) {
        this.$message.error(e.response?.data?.message || '加载失败')
      } finally {
        this.loading = false
      }
    },
    openBatchDialog() {
      this.batchForm = { userUuid: '', userId: null, count: 5 }
      this.batchDialogVisible = true
    },
    async submitBatch() {
      const forAll = !this.batchForm.userUuid?.trim() && !this.batchForm.userId
      if (forAll) {
        try {
          await this.$confirm(
            `将为全部用户各生成 ${this.batchForm.count} 个邀请码，可能耗时较长，是否继续？`,
            '确认全员发放',
            { type: 'warning' }
          )
        } catch {
          return
        }
      }
      this.batchSubmitting = true
      try {
        const payload = { count: this.batchForm.count }
        if (this.batchForm.userUuid?.trim()) {
          payload.userUuid = this.batchForm.userUuid.trim()
        }
        if (this.batchForm.userId) {
          payload.userId = this.batchForm.userId
        }
        const res = await axios.post('/api/v1/admin/invite/code/create', payload, { headers: authHeaders() })
        if (res.data.code !== 200) {
          this.$message.error(res.data.message || '生成失败')
          return
        }
        const data = res.data.data || {}
        if (data.status === 'processing' && data.batchId) {
          this.$message.success(res.data.message || '任务已提交')
          this.batchDialogVisible = false
          this.startBatchPoll(data.batchId)
          return
        }
        this.$message.success(res.data.message || '生成成功')
        this.batchDialogVisible = false
        this.fetchList()
      } catch (e) {
        this.$message.error(e.response?.data?.message || '生成失败')
      } finally {
        this.batchSubmitting = false
      }
    },
    startBatchPoll(batchId) {
      this.stopBatchPoll()
      this.batchProgress = { batchId, status: 'processing', message: '后台处理中…', usersProcessed: 0, totalUsers: 0, codesCreated: 0 }
      this.batchProgressVisible = true
      const poll = async () => {
        try {
          const res = await axios.post(
            '/api/v1/admin/invite/code/batch/status',
            { batchId },
            { headers: authHeaders() }
          )
          if (res.data.code === 200 && res.data.data) {
            this.batchProgress = res.data.data
            if (res.data.data.status === 'done') {
              this.$message.success(res.data.data.message || '批量发放完成')
              this.stopBatchPoll()
              this.fetchList()
            } else if (res.data.data.status === 'failed') {
              this.$message.error(res.data.data.message || '任务失败')
              this.stopBatchPoll()
            }
          }
        } catch (_) { /* 忽略单次轮询失败 */ }
      }
      poll()
      this.batchPollTimer = setInterval(poll, 2000)
    },
    stopBatchPoll() {
      if (this.batchPollTimer) {
        clearInterval(this.batchPollTimer)
        this.batchPollTimer = null
      }
    },
    closeBatchProgress() {
      this.stopBatchPoll()
      this.batchProgressVisible = false
      this.fetchList()
    }
  }
}
</script>

<style scoped>
.filter-input { width: 160px; }
.filter-input-wide { width: 220px; }
.filter-select { width: 130px; }
.muted { color: #909399; font-size: 12px; }
.uuid-line { word-break: break-all; margin-top: 2px; }
.progress-detail { margin-top: 12px; }
</style>
